import { SelectChangeEvent } from '@mui/material/Select';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { ClientRoleQueryDto } from '@bom-nextgen-keycloak/models';
import {
  CONFIG,
  selectPermissionRole,
  selectPermissionRoleDetail,
  useAlertMessage,
  useNotification,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  deleteClientRole,
  ErrorMessage,
  fetchClientRole,
  QUERY_KEY,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  SearchboxEmit,
  SearchboxOptions,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  checkProcessing,
  generateHash,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';

type RoleType = string;

type Arguments = {
  callback?: () => void;
  disableQuery?: boolean;
};

const useRole2 = (options?: Arguments) => {
  const callback = options?.callback;
  const disableQuery = options?.disableQuery;
  const enableQuery = !disableQuery;
  const resource = useResource();
  const { clientId } = resource;
  const defaultParams = {
    first: CONFIG.pagination.offset,
    max: CONFIG.pagination.step,
    search: '',
  };
  const defaultData = {
    items: [],
    totalCount: 0,
    totalRecord: 0,
  };
  const defaultSelection = {
    item: null,
  };
  const queryClient = useQueryClient();
  const { setAlertMessage } = useAlertMessage();
  const { setNotification } = useNotification();
  const { canViewAdd: canAdd, canViewDelete: canDelete } =
    useSelector(selectPermissionRole);
  const { canViewPage: canEdit } = useSelector(selectPermissionRoleDetail);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [params, setParams] = useState<ClientRoleQueryDto>({
    ...defaultParams,
  });
  const [selectedItem, setSelectedItem] = useState<RoleType | null>(
    defaultSelection.item
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [currentOffset, setCurrentOffset] = useState(CONFIG.pagination.offset);
  const [previousOffset, setPreviousOffset] = useState(
    CONFIG.pagination.offset
  );
  const [pageStep, setPageStep] = useState(CONFIG.pagination.step);
  const [queryHash, setQueryHash] = useState(generateHash());
  const [isSearching, setIsSearching] = useState(false);
  const [wasSearch, setWasSearch] = useState(false);
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    CONFIG.ui.debounceDelay
  );

  const isSearch = searchQuery !== '';
  const queryKeys = [
    QUERY_KEY.CLIENT_ROLE_LIST,
    clientId,
    queryHash,
    debouncedSearchQuery,
  ];

  const {
    data = defaultData,
    isFetched,
    isFetching,
    isLoading,
  } = useQuery(
    queryKeys,
    () => fetchClientRole(clientId, { ...params, search: searchQuery }),
    {
      enabled: enableQuery,
      onError: (error: AxiosError<ErrorMessage>) => {
        const message = error.response?.data.message || 'Cannot get roles';

        setAlertMessage({
          message,
          statusCode: error.response?.status,
          typeStatusMessage: 'error',
        });
      },
      onSuccess: () => {
        if (isSearch && isSearching) {
          setIsSearching(false);
        }

        if (wasSearch && !isSearch) {
          setWasSearch(false);
        }

        if (!isSearch) {
          setPreviousOffset(params.first);
        }
      },
      staleTime: CONFIG.query.staleTime,
    }
  );

  const isProcessing = checkProcessing({ isFetched, isFetching, isLoading });

  const deleteMutation = useMutation(deleteClientRole, {
    onError: (error: AxiosError<ErrorMessage>) => {
      const message = error.response?.data.message || 'Cannot delete role';

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSuccess: () => {
      setIsDeleteDialogOpen(false);
      setNotification({
        message: 'Role deleted',
        type: 'success',
      });

      if (enableQuery) {
        handleSelectionReset();
        handleReload();
      }

      callback && callback();
    },
  });

  const handleSelectionReset = () => {
    setSelectedItem(defaultSelection.item);
  };

  const handleRefetch = () => {
    setQueryHash(generateHash());
  };

  const handleReload = () => {
    queryClient.invalidateQueries(queryKeys);
  };

  const handleRefresh = () => {
    const offset = 0;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));

    handleRefetch();
  };

  const handleCreateRequest = () => {
    setIsCreateDialogOpen(true);
  };

  const handleDeleteRequest = (data: RoleType) => {
    if (data) {
      setSelectedItem(data);
      setIsDeleteDialogOpen(true);
    } else {
      // TODO: Implement a handler
    }
  };

  const handleCreateCancel = () => {
    setIsCreateDialogOpen(false);
    handleSelectionReset();
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    handleSelectionReset();
  };

  const handleCreateAction = () => {
    setIsCreateDialogOpen(false);
    handleSelectionReset();
    handleReload();
  };

  const handleDeleteAction = () => {
    if (selectedItem) {
      deleteMutation.mutate({
        clientId,
        roleName: selectedItem,
      });
    } else {
      // TODO: Implement a handler
    }
  };

  const handleSearch = ({ event, isEnter = false }: SearchboxOptions) => {
    const { value } = event;
    const keyword = value.trim();
    const hasKeyword = keyword !== '';
    const hasChanged = keyword !== '' && keyword !== searchQuery;
    const offset = hasKeyword ? 0 : previousOffset;

    setCurrentOffset(offset);
    setIsSearching(hasChanged);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));

    if (isEnter && hasKeyword) {
      handleRefetch();
    } else {
      setSearchQuery((previous) => {
        setWasSearch(previous !== '' && !hasKeyword);

        return value;
      });
    }
  };

  const handleSearchChange = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: false });
  };

  const handleSearchEnter = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: true });
  };

  const handleSearchReset = () => {
    const value = '';
    const offset = previousOffset;

    setCurrentOffset(offset);
    setWasSearch(true);
    setParams((previous) => ({
      ...defaultParams,
      first: offset,
      max: previous.max,
      search: value,
    }));
    setSearchQuery(value);
  };

  const handleSearchClear = () => {
    const value = '';
    const hasKeyword = isSearch;

    if (hasKeyword) {
      handleSearchReset();
    } else {
      // NOTE: Clear whitespaces without making an API request
      setSearchQuery(value);
    }
  };

  const handlePageChange = (offset: number) => {
    if (wasSearch) {
      setWasSearch(false);
    }

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));
    handleRefetch();
  };

  const handleStepChange = (event: SelectChangeEvent<unknown>) => {
    const step = event.target.value as number;
    const offset = 0;

    setCurrentOffset(offset);
    setPreviousOffset(offset);
    setPageStep(step);
    setParams((previous) => ({
      ...previous,
      first: offset,
      max: step,
    }));
    handleRefetch();
  };

  return {
    canAdd,
    canDelete,
    canEdit,
    data,
    handleCreateAction,
    handleCreateCancel,
    handleCreateRequest,
    handleDeleteAction,
    handleDeleteCancel,
    handleDeleteRequest,
    handlePageChange,
    handleRefresh,
    handleSearchChange,
    handleSearchClear,
    handleSearchEnter,
    handleSearchReset,
    handleStepChange,
    isCreateDialogOpen,
    isDeleteDialogOpen,
    isDeleting: deleteMutation.isLoading,
    isFetched,
    isFetching,
    isLoading,
    isProcessing,
    isSearch,
    isSearching,
    paginationOffset: currentOffset,
    paginationStep: pageStep,
    searchQuery,
    selectedItem,
    wasSearch,
  };
};

export { useRole2 };
